import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { handleError, ERROR_CATEGORY } from '../../../utils/ErrorUtils';

/**
 * Status Bar Component for Roll20-style status information
 * Displays current tool, coordinates, zoom, selection info, and system status
 */
const StatusBar = ({
  currentTool = 'select',
  mousePosition = { x: 0, y: 0 },
  zoom = 1.0,
  selectedTokens = [],
  selectedDrawings = [],
  gridSize = 50,
  snapToGrid = false,
  isConnected = true,
  lastSaved = null,
  hasUnsavedChanges = false,
  onSave = null,
  className = ''
}) => {
  // Format coordinates for display
  const formattedCoords = useMemo(() => {
    if (snapToGrid) {
      const gridX = Math.round(mousePosition.x / gridSize) * gridSize;
      const gridY = Math.round(mousePosition.y / gridSize) * gridSize;
      return `${gridX}, ${gridY}`;
    }
    return `${Math.round(mousePosition.x)}, ${Math.round(mousePosition.y)}`;
  }, [mousePosition, snapToGrid, gridSize]);

  // Format zoom percentage
  const zoomPercentage = useMemo(() => {
    return `${Math.round(zoom * 100)}%`;
  }, [zoom]);

  // Format selection info
  const selectionInfo = useMemo(() => {
    const tokenCount = selectedTokens.length;
    const drawingCount = selectedDrawings.length;
    const total = tokenCount + drawingCount;
    
    if (total === 0) return 'Nothing selected';
    if (total === 1) {
      if (tokenCount === 1) return '1 token selected';
      if (drawingCount === 1) return '1 drawing selected';
    }
    
    const parts = [];
    if (tokenCount > 0) parts.push(`${tokenCount} token${tokenCount > 1 ? 's' : ''}`);
    if (drawingCount > 0) parts.push(`${drawingCount} drawing${drawingCount > 1 ? 's' : ''}`);
    
    return `${parts.join(', ')} selected`;
  }, [selectedTokens, selectedDrawings]);

  // Format last saved time
  const lastSavedText = useMemo(() => {
    if (!lastSaved) return 'Never saved';
    
    const now = new Date();
    const saved = new Date(lastSaved);
    const diffMs = now - saved;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just saved';
    if (diffMins < 60) return `Saved ${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Saved ${diffHours}h ago`;
    
    return saved.toLocaleDateString();
  }, [lastSaved]);

  // Tool display names
  const TOOL_NAMES = {
    select: 'Select',
    pan: 'Pan',
    freehand: 'Freehand',
    line: 'Line',
    rectangle: 'Rectangle',
    circle: 'Circle',
    ellipse: 'Ellipse',
    polygon: 'Polygon',
    arrow: 'Arrow',
    text: 'Text',
    eraser: 'Eraser',
    measure: 'Measure',
    token: 'Token'
  };

  // Handle save action
  const handleSave = useCallback(() => {
    try {
      if (onSave) {
        onSave();
      }
    } catch (error) {
      handleError(error, false, {
        category: ERROR_CATEGORY.GENERAL,
        message: 'Failed to save session'
      });
    }
  }, [onSave]);

  return (
    <div className={`status-bar bg-gray-800 border-t border-gray-600 px-4 py-2 flex items-center justify-between text-sm text-gray-300 ${className}`}>
      {/* Left Section - Tool and Coordinates */}
      <div className="flex items-center space-x-6">
        {/* Current Tool */}
        <div className="flex items-center space-x-2">
          <span className="text-gray-400">Tool:</span>
          <span className="font-medium text-white">
            {TOOL_NAMES[currentTool] || currentTool}
          </span>
        </div>

        {/* Mouse Coordinates */}
        <div className="flex items-center space-x-2">
          <span className="text-gray-400">Position:</span>
          <span className="font-mono text-white">{formattedCoords}</span>
          {snapToGrid && (
            <span className="text-xs text-blue-400 bg-blue-900 px-1 rounded">
              SNAP
            </span>
          )}
        </div>

        {/* Zoom Level */}
        <div className="flex items-center space-x-2">
          <span className="text-gray-400">Zoom:</span>
          <span className="font-mono text-white">{zoomPercentage}</span>
        </div>
      </div>

      {/* Center Section - Selection Info */}
      <div className="flex items-center">
        <span className="text-gray-300">{selectionInfo}</span>
      </div>

      {/* Right Section - Connection and Save Status */}
      <div className="flex items-center space-x-6">
        {/* Connection Status */}
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${
            isConnected ? 'bg-green-400' : 'bg-red-400'
          }`} />
          <span className={isConnected ? 'text-green-400' : 'text-red-400'}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>

        {/* Save Status */}
        <div className="flex items-center space-x-2">
          {hasUnsavedChanges && (
            <button
              onClick={handleSave}
              className="text-yellow-400 hover:text-yellow-300 transition-colors"
              title="Save changes"
            >
              💾 Save
            </button>
          )}
          <span className={`text-xs ${
            hasUnsavedChanges ? 'text-yellow-400' : 'text-gray-400'
          }`}>
            {hasUnsavedChanges ? 'Unsaved changes' : lastSavedText}
          </span>
        </div>

        {/* Grid Info */}
        <div className="flex items-center space-x-2">
          <span className="text-gray-400">Grid:</span>
          <span className="font-mono text-white">{gridSize}px</span>
        </div>
      </div>
    </div>
  );
};

StatusBar.propTypes = {
  currentTool: PropTypes.string,
  mousePosition: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired
  }),
  zoom: PropTypes.number,
  selectedTokens: PropTypes.array,
  selectedDrawings: PropTypes.array,
  gridSize: PropTypes.number,
  snapToGrid: PropTypes.bool,
  isConnected: PropTypes.bool,
  lastSaved: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  hasUnsavedChanges: PropTypes.bool,
  onSave: PropTypes.func,
  className: PropTypes.string
};

export default StatusBar;
